<script setup lang="ts">
import { ref, watch, type Ref } from 'vue'
import GbButton from '@/components/GbButton.vue'
import GbDialog from '@/components/GbDialog.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import { deleteFolderResource } from '@/library/api'

const props = defineProps<{
  externalOpen: boolean
  contentUuid: string
  folderUuid: string
  attachmentName: string
}>()

const emit = defineEmits<{
  'update:externalOpen': [value: boolean]
  deleted: []
}>()

const visibleRef: Ref<boolean> = ref(props.externalOpen)
const isDeleting = ref(false)
const deleteError = ref(false)

watch(
  () => props.externalOpen,
  (val) => {
    visibleRef.value = val
    if (val) {
      deleteError.value = false
    }
  }
)

watch(
  () => visibleRef.value,
  (val) => { emit('update:externalOpen', val) }
)

const closeModal = (): void => {
  visibleRef.value = false
}

const confirmDelete = async (): Promise<void> => {
  deleteError.value = false
  isDeleting.value = true
  try {
    await deleteFolderResource(props.folderUuid, props.contentUuid, 'attachment_file')
    closeModal()
    emit('deleted')
  } catch (e) {
    deleteError.value = true
    console.error('Failed to delete attachment', e)
  } finally {
    isDeleting.value = false
  }
}
</script>

<template>
  <GbDialog
    dialogId="delete-attachment-modal"
    v-model:visible="visibleRef"
    header="Delete attachment"
    :footer-buttons="true"
  >
    <template #bodyContent>
      <div class="flex flex-col gap-2">
        <p>Are you sure you want to delete this attachment? This action is irreversible.</p>
        <div class="rounded px-3 py-2 text-gray-700 text-[15px] font-normal truncate bg-gray-50">
          {{ attachmentName }}
        </div>
        <GbInlineMessage gbTheme="red" v-if="deleteError">
          We couldn't delete the attachment. Try again later.
        </GbInlineMessage>
      </div>
    </template>
    <template #footerButtonLeft>
      <GbButton
        type="button"
        gbTheme="grayOutlineSecondary"
        label="Cancel"
        :disabled="isDeleting"
        @click="closeModal"
      />
    </template>
    <template #footerButtonRight>
      <GbButton
        type="button"
        gbTheme="redPrimary"
        label="Delete"
        :disabled="isDeleting"
        @click="confirmDelete"
      />
    </template>
  </GbDialog>
</template>
